import { mdiClose } from '@mdi/js';
import { createInjectionState } from '@vueuse/core';
import { type AxiosError, type AxiosResponse, isAxiosError, isCancel } from 'axios';
import { type GenericObject, type Path } from 'vee-validate';
import { toast } from 'vuetify-sonner';

import type { ApiResponse } from '@abaninja/types';

import { useGlobalI18n } from '@abaninja/util-i18n';
import { objectKeys } from '@abaninja/util-object';

const { global: { t } } = useGlobalI18n();

export type Notification = {
  description?: string;
  message: string;
  timeout: number;
  type: 'error' | 'info' | 'success' | 'warning';
};

interface VeeValidateInvalidSubmissionErrorContext<TInput extends GenericObject> {
  errors: Partial<Record<Path<TInput>, string>>;
}

function showNotification(
  notification: Partial<Notification> & Pick<Notification, 'message'> & { options?: Parameters<typeof toast>[1] },
) {
  const type = notification.type ?? 'success';

  toast(notification.message, {
    description: notification.description,
    duration: notification.timeout ?? 5000,
    prependIcon: `$${type}`,
    action: {
      buttonProps: {
        icon: mdiClose,
        size: 'x-small',
        variant: 'text',
        ...notification.options?.action?.buttonProps,
      },
    },
    cardProps: {
      border: false,
      color: type,
      elevation: 0,
      variant: 'tonal',
      width: 'var(--width)',
      style: {
        background: 'rgba(var(--v-theme-background))',
      },
      ...notification.options?.cardProps,
    },
  });
}

function fromAxiosResponse(axiosResponse: AxiosResponse<ApiResponse<unknown>>) {
  if (axiosResponse.data.message) {
    showNotification({
      message: axiosResponse.data.message,
    });
  }
}

function fromPayload({ message }: ApiResponse<unknown>) {
  if (message) {
    showNotification({
      message,
    });
  }
}

async function messageFromBlob(axiosError: AxiosError) {
  if (axiosError.response?.data instanceof Blob) {
    try {
      const responseData = await axiosError.response.data.text();

      return JSON.parse(responseData)?.message;
    } catch {
    // Blob parsing failed
    }
  }

  return undefined;
}

async function handleMessageFromAxiosError(axiosError: AxiosError<ApiResponse<ErrorPayload>>) {
  const message
    = await messageFromBlob(axiosError)
      ?? axiosError.response?.data.message
      ?? axiosError.message;

  showNotification({
    message,
    type: 'error',
  });
}

function fromAxiosError(axiosError: unknown) {
  if (!isCancel(axiosError) && isAxiosError<ApiResponse<ErrorPayload>>(axiosError)) {
    void handleMessageFromAxiosError(axiosError);
  }
}

async function executeWithNotification<T extends ApiResponse<unknown>>(promise: Promise<T>) {
  try {
    const result = await promise;

    fromPayload(result);

    return result;
  } catch (error) {
    fromAxiosError(error);

    throw error;
  }
}

function showVeeValidateInvalidSubmission<TInput extends GenericObject = GenericObject>(
  data: VeeValidateInvalidSubmissionErrorContext<TInput>,
) {
  showNotification({
    message: t('messages.fieldIsNotCorrect', objectKeys(data.errors).length),
    type: 'error',
  });
}

const [useProvideNotificationContext, useNotificationContext] = createInjectionState(() => ({
  executeWithNotification,
  fromAxiosError,
  fromAxiosResponse,
  fromPayload,
  showNotification,
  showVeeValidateInvalidSubmission,
}));

export { useProvideNotificationContext };

export function useNotification() {
  const notification = useNotificationContext();

  if (!notification) {
    throw new Error('Please call `useProvideNotificationContext` on the appropriate parent component');
  }

  return notification;
}
